import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import type { Event } from '~/shared/types/event'

export function eventFeaturesSummary(event: Event): string {
  const liveRoom = event.liveRoomMode === 'off' ? m.featureOff() : m.featureOn()
  const parts = [`${m.liveRoomFeature()} ${liveRoom}`]
  if (event.roomVisibility === 'request') parts.push(m.eventVisibilityRequest())
  if (event.membersOnly) parts.push(m.eventVisibilityMembers())
  if (event.rsvpEnabled) parts.push(m.rsvpFeature())
  if (event.plusOnesMax > 0) parts.push(m.plusOnesUpToCount({ count: event.plusOnesMax }))
  if (event.minAge != null) parts.push(m.minAgeBadge({ age: String(event.minAge) }))
  if (event.studentsOnly) parts.push(m.studentsOnlyFeature())
  return parts.join(' · ')
}

export function EventFeatureRows({ event }: { event: Event }) {
  return (
    <div className="space-y-3">
      <FeatureStatusRow label={m.liveRoomFeature()} enabled={event.liveRoomMode !== 'off'} />
      {event.membersOnly && <FeatureStatusRow label={m.eventVisibilityMembers()} enabled />}
      <FeatureStatusRow
        label={m.eventVisibilityRequest()}
        enabled={event.roomVisibility === 'request'}
      />
      {event.roomVisibility === 'request' && (
        <FeatureStatusRow label={m.requestEmailsFeature()} enabled={event.requestEmails} />
      )}
      <FeatureStatusRow label={m.rsvpFeature()} enabled={event.rsvpEnabled} />
      <FeatureStatusRow
        label={m.plusOnesFeature()}
        enabled={event.plusOnesMax > 0}
        detail={
          event.plusOnesMax > 0 ? m.plusOnesUpToCount({ count: event.plusOnesMax }) : undefined
        }
      />
      <FeatureStatusRow
        label={m.minAgeFeature()}
        enabled={event.minAge != null}
        detail={
          event.minAge == null ? m.minAgeEveryone() : m.minAgeBadge({ age: String(event.minAge) })
        }
      />
      <FeatureStatusRow label={m.studentsOnlyFeature()} enabled={event.studentsOnly} />
      {event.audienceNote && <p className="text-sm text-muted-foreground">{event.audienceNote}</p>}
      {(event.targetAgeMin != null || event.targetAgeMax != null) && (
        <div className="flex items-center justify-between gap-4 text-sm">
          <span>{m.targetAgeLabel()}</span>
          <span className="font-medium">
            {event.targetAgeMin ?? '…'}–{event.targetAgeMax ?? '…'}
          </span>
        </div>
      )}
    </div>
  )
}

function FeatureStatusRow({
  label,
  enabled,
  detail,
}: {
  label: string
  enabled: boolean
  detail?: string
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span>{label}</span>
      <span
        className={cn(
          'inline-flex items-center gap-2',
          enabled ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        <span
          aria-hidden
          className={cn('size-2 rounded-full', enabled ? 'bg-brand' : 'bg-muted-foreground/40')}
        />
        {detail ?? (enabled ? m.featureOn() : m.featureOff())}
      </span>
    </div>
  )
}
